import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Grid } from "./Home";
import { Marquee } from "@/components/Marquee";
import { useSeo } from "@/hooks/useSeo";

const HERO = "/products/street-sheriff/men-olive-black-utility-jacket-back.jpeg";

export default function StreetSheriff() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { collection: "street-sheriff", limit: 100 } })
      .then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const itemList = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Street Sheriff by HYEAMAN",
    url: "https://hyeamanbrand.com/street-sheriff",
    description: "Street Sheriff by HYEAMAN — global luxury streetwear. Utility outerwear, cargo tailoring and the gold globe monogram. Wear Confidence. No One Is Left Behind.",
    isPartOf: { "@type": "WebSite", name: "HYEAMAN", url: "https://hyeamanbrand.com" },
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: products.length,
      itemListElement: products.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://hyeamanbrand.com/product/${p.slug}`,
        name: p.name,
      })),
    },
  };

  useSeo({
    title: "Street Sheriff by HYEAMAN | Global Luxury Streetwear",
    description: "Discover Street Sheriff by HYEAMAN — global luxury streetwear built as urban armour. Utility jackets, cargo tailoring, denim and track sets with the gold globe monogram.",
    path: "/street-sheriff",
    image: HERO,
    jsonLd: itemList,
  });

  return (
    <div data-testid="street-sheriff-page">
      {/* HERO */}
      <section className="relative flex min-h-[70vh] items-end overflow-hidden">
        <img src={HERO} alt="Street Sheriff by HYEAMAN collection" className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/60" />
        <div className="relative mx-auto w-full max-w-7xl px-4 pb-16 sm:px-8 lg:px-12">
          <p className="overline-label mb-3">HYEAMAN · Street Sheriff</p>
          <h1 className="font-black uppercase leading-[0.9] tracking-tight text-cream text-5xl sm:text-7xl">
            Street <span className="font-serif font-normal italic text-gradient-gold">Sheriff</span>
          </h1>
          <p className="mt-6 max-w-xl text-cream/70">
            Global luxury streetwear built as urban armour. Street Sheriff is the second name of HYEAMAN — protecting unity and claiming the streets for everyone.
          </p>
          <p className="mt-4 font-mono text-[0.7rem] uppercase tracking-[0.35em] text-gold/70">Wear Confidence · No One Is Left Behind</p>
        </div>
      </section>

      <Marquee />

      {/* STORY */}
      <section className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-8" data-testid="ss-story">
        <p className="overline-label mb-4">The Collective</p>
        <p className="font-serif text-2xl italic leading-relaxed text-cream">
          "Different backgrounds. Same streets. Welcome to the Nations."
        </p>
        <p className="mt-6 text-cream/70">
          The Street Sheriff is the guardian of our global melting pot — bold authority, gritty resilience and raw energy, finished with the gold globe monogram. One collection, many cultures, nobody left behind.
        </p>
      </section>

      {/* GRID */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-8 lg:px-12">
        <div className="mb-8 flex items-end justify-between">
          <h2 className="font-black uppercase tracking-tight text-cream text-3xl sm:text-4xl">The Collection</h2>
          <p className="text-sm text-cream/50" data-testid="ss-count">{products.length} pieces</p>
        </div>
        {products.length === 0 ? (
          <p className="text-cream/50" data-testid="ss-empty">Collection coming soon.</p>
        ) : (
          <Grid>{products.map((p, i) => <ProductCard key={p.id} product={p} index={i} ribbon="Street Sheriff" />)}</Grid>
        )}
        <div className="mt-12 flex flex-wrap gap-4">
          <Link to="/shop?collection=street-sheriff" data-testid="ss-shop-cta" className="bg-gold px-8 py-4 text-xs font-bold uppercase tracking-widest text-black transition hover:bg-gold-bright">Shop Street Sheriff</Link>
          <Link to="/about" className="border border-gold/60 px-8 py-4 text-xs font-bold uppercase tracking-widest text-cream transition hover:bg-gold/10">The HYEAMAN Story</Link>
        </div>
      </section>
    </div>
  );
}
